import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, can, refusalReason } from '../../lib/api'
import { exceptionsApi, type DDCatalogue, type DDParameter } from '../../lib/exceptions'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip } from '../../components/kit'
import '../../styles/prose.css'
import '../../styles/exceptions.css'

// The due diligence parameters: what an exception is raised under, the risk
// types each covers and the function that reviews it (decision 1 of the wave 3
// plan). Six ship; the officer renames, retires and adds.
//
// A parameter is never deleted: retiring it takes it off the raise form and
// leaves every exception raised under it saying so. Saving replaces the whole
// catalogue in one act, which the API records with the catalogue before and
// after, so the order on this screen is the order the raise form offers.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

export function DueDiligenceSettings() {
  const [cat, setCat] = useState<DDCatalogue | null>(null)
  const [rows, setRows] = useState<DDParameter[]>([])
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const canWrite = can('programme.write')

  const load = useCallback(() => {
    exceptionsApi
      .catalogue()
      .then((c) => {
        setCat(c)
        setRows(c.parameters)
        setDirty(false)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  const reviewers = useMemo(() => (cat?.reviewers ?? []).filter((r) => r.canReview), [cat])
  const deciders = useMemo(() => (cat?.deciders ?? []).filter((r) => r.canDecide), [cat])

  function change(i: number, patch: Partial<DDParameter>) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
    setDirty(true)
    setSaved(false)
  }

  function move(i: number, by: number) {
    setRows((rs) => {
      const j = i + by
      const a = rs[i]
      const b = rs[j]
      if (!a || !b) return rs
      const next = [...rs]
      next[i] = b
      next[j] = a
      return next
    })
    setDirty(true)
    setSaved(false)
  }

  function add() {
    setRows((rs) => [...rs, { code: '', name: '', riskTypes: [], reviewerPersona: 'tprm_lead', active: true }])
    setDirty(true)
    setSaved(false)
  }

  function save() {
    setSaving(true)
    setSaveError(null)
    setProblems([])
    exceptionsApi
      .saveCatalogue(rows)
      .then((c) => {
        setCat(c)
        setRows(c.parameters)
        setDirty(false)
        setSaved(true)
      })
      .catch((err) => {
        const ae = asApiError(err)
        const body = ae.body as { problems?: string[] } | null
        if (body?.problems?.length) setProblems(body.problems)
        else setSaveError(ae)
      })
      .finally(() => setSaving(false))
  }

  if (error) return <ErrorBox error={error} what="The exception parameters" />
  if (!cat) return <Loading what="the exception parameters" />

  const inUse = rows.filter((r) => r.active).length

  return (
    <div className="prose-page dd-page">
      <div className="prose-head">
        <div className="prose-head-main">
          <div className="prose-title-row">
            {/* "Exception parameters" (release 3.3, C8 of the 12 Oct plan):
                "Due diligence" named both the questionnaire stage and this
                catalogue. The screen's id and address stay due-diligence. */}
            <h1>Exception parameters</h1>
            <Toggletip label="About the parameters">
              An exception is raised under one of these. The function named reviews it before the authority for its
              severity decides.
            </Toggletip>
          </div>
          <p className="prose-meta">
            <span>{inUse} in use</span>
            <a href="#/settings/exception-authority">Who approves by severity</a>
          </p>
        </div>
        <div className="prose-actions">
          <button
            type="button"
            className="primary"
            disabled={!canWrite || !dirty || saving}
            title={canWrite ? undefined : refusalReason('programme.write')}
            onClick={save}
            data-action="tprm.due-diligence.a01"
          >
            {saving ? 'Saving' : 'Save changes'}
          </button>
        </div>
      </div>

      {saved && (
        <p className="prose-line" role="status">
          <Status tone="ok">Saved</Status>
          The raise form offers these now.
        </p>
      )}
      {problems.length > 0 && (
        <div className="form-errors" role="alert">
          <h3>Not saved. Fix these first</h3>
          <ol>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
        </div>
      )}
      {saveError && <ErrorBox error={saveError} what="The parameters" verb="saved" />}

      <section className="panel panel-flush" aria-labelledby="dd-list-h">
        <div className="prose-panelhead">
          <h2 id="dd-list-h">Parameters</h2>
        </div>
        <ol className="dd-list">
          {rows.map((p, i) => (
            <li key={p.code || 'new-' + i} className={'dd-row' + (p.active ? '' : ' dd-retired')}>
              <div className="dd-order">
                <button type="button" aria-label={'Move ' + (p.name || 'this parameter') + ' up'} disabled={!canWrite || i === 0} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={'Move ' + (p.name || 'this parameter') + ' down'}
                  disabled={!canWrite || i === rows.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </button>
              </div>
              <div className="dd-main">
                <label className="dd-field">
                  <span>Name</span>
                  <input
                    type="text"
                    value={p.name}
                    disabled={!canWrite}
                    maxLength={120}
                    onChange={(e) => change(i, { name: e.target.value })}
                  />
                </label>
                <fieldset className="dd-types" disabled={!canWrite}>
                  <legend>Risk types</legend>
                  <div className="dd-chips">
                    {cat.riskTypes.map((t) => {
                      const on = p.riskTypes.includes(t.code)
                      return (
                        <button
                          key={t.code}
                          type="button"
                          className="chip-toggle"
                          aria-pressed={on}
                          onClick={() =>
                            change(i, {
                              riskTypes: on ? p.riskTypes.filter((x) => x !== t.code) : [...p.riskTypes, t.code],
                            })
                          }
                        >
                          {t.name}
                        </button>
                      )
                    })}
                  </div>
                </fieldset>
              </div>
              <div className="dd-side">
                <label className="dd-field">
                  <span>Reviewed by</span>
                  <select
                    value={p.reviewerPersona}
                    disabled={!canWrite}
                    onChange={(e) => change(i, { reviewerPersona: e.target.value })}
                  >
                    {reviewers.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dd-field">
                  <span>
                    Decided by{' '}
                    <Toggletip label="Who decides">
                      The function that decides this parameter's exceptions in the risk officer's place, wherever the
                      authority for the severity names the risk officer. The rest of the ladder is unchanged: a high
                      gap still needs the CISO, a critical one the executive.
                    </Toggletip>
                  </span>
                  <select
                    value={p.deciderPersona ?? ''}
                    disabled={!canWrite}
                    onChange={(e) => change(i, { deciderPersona: e.target.value })}
                  >
                    <option value="">The authority as it stands</option>
                    {deciders.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dd-check">
                  <input
                    type="checkbox"
                    checked={p.active}
                    disabled={!canWrite}
                    onChange={(e) => change(i, { active: e.target.checked })}
                  />
                  In use
                </label>
              </div>
            </li>
          ))}
        </ol>
        <div className="dd-foot">
          <button type="button" disabled={!canWrite} onClick={add}>
            Add a parameter
          </button>
          <Toggletip label="Who may review">
            A reviewer has to be able to record a review. Legal counsel reviews legal and regulatory gaps only (a
            parameter covering legal and compliance risk); since release 3.1 it reviews legal, regulatory and
            compliance and the compliance officer decides it.
          </Toggletip>
        </div>
      </section>
    </div>
  )
}
